// src/pages/ClientesList.jsx (Adaptada para Trabajador)
import React, { useState, useEffect } from 'react';
import { get, patch } from '../../helpers/apiHelper';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import useAuthStore from '../../store/authStore';
import Pagination from '../../components/common/Pagination';
import { FaEdit, FaStar } from 'react-icons/fa';
import EditClienteModal from '../../components/modals/EditClienteModal';
import AccumulatePointsModal from '../../components/modals/AccumulatePointsModal';
import QuickAccumulateModal from '../../components/modals/QuickAccumulateModal';
import RedeemPremiosModal from '../../components/modals/RedeemPremiosModal';
import RedeemRewardButton from '../../components/rewards/RedeemRewardButton';


function ClientesList() {
  const { tiendaId, trabajadorId, franquiciadoId } = useParams();
  const [clientes, setClientes] = useState([]);
  const { user, trabajador, franquiciado } = useAuthStore();
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(10);
  const [editingCliente, setEditingCliente] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAccumulateModal, setShowAccumulateModal] = useState(false);
  const [showQuickAccumulateModal, setShowQuickAccumulateModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [montoConsumido, setMontoConsumido] = useState(0);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [marcaCompleta, setMarcaCompleta] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClientes();
    fetchProductos();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchTerm, clientes]);

  const fetchClientes = async () => {
    try {
      const response = await get(`clientes/tienda/${tiendaId}`);
      setClientes(response);
      setFilteredClientes(response);
    } catch (error) {
      console.error('🔍 [ClientesList] Error fetching clientes:', error);
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const fetchProductos = async () => {
    setLoadingProductos(true);
    try {
      let marcas = [];

      // Determinar si es usuario normal, trabajador o franquiciado
      if (user && user.userId) {
        // Usuario normal
        marcas = await get(`marcas/user/${user.userId}`);
      } else if (trabajador && trabajador.trabajadorId) {
        // Trabajador - obtener marcas a través de las tiendas asociadas
        const tiendas = await get(`trabajadores/${trabajador.trabajadorId}/tiendas`);

        if (tiendas.length > 0) {
          // Obtener la marca de la tienda específica
          const tienda = tiendas.find(t => t._id === tiendaId);

          if (tienda && tienda.marca) {
            marcas = [tienda.marca];
          }
        }
      } else if (franquiciado && franquiciado.franquiciadoId) {
        // Franquiciado - obtener marcas a través de las tiendas asociadas
        const tiendas = await get(`franquiciados/${franquiciado.franquiciadoId}/tiendas`);

        if (tiendas.length > 0) {
          // Obtener la marca de la tienda específica
          const tienda = tiendas.find(t => t._id === tiendaId);

          if (tienda && tienda.marca) {
            marcas = [tienda.marca];
          }
        }
      }

      if (marcas.length > 0) {
        // Para trabajadores y franquiciados, marcas[0] ya es el ID de la marca, no el objeto completo
        const marcaId = typeof marcas[0] === 'string' ? marcas[0] : marcas[0]._id;

        // Usar el nuevo endpoint que trae productos de la marca (sin filtro activo temporalmente)
        const response = await get(`productos/${marcaId}/lista`);
        setProductos(response);
      } else {
        setProductos([]);
      }
    } catch (error) {
      console.error('Error fetching productos:', error);

      // Solo mostrar error si no es un error de servidor interno
      if (error.message && !error.message.includes('Internal server error')) {
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los productos. Intente de nuevo.',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    } finally {
      setLoadingProductos(false);
    }
  };

  const handleSearch = () => {
    const results = clientes.filter(cliente =>
      (cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (cliente.email && cliente.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (cliente.telefono && cliente.telefono.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredClientes(results);
  };

  const handleCreateCliente = () => {
    if (trabajadorId) {
      navigate(`/trabajador/${trabajadorId}/tiendas/${tiendaId}/clientes/create`);
    } else if (franquiciadoId) {
      navigate(`/franquiciado/${franquiciadoId}/tiendas/${tiendaId}/clientes/create`);
    } else {
      navigate(`/tiendas/${tiendaId}/clientes/create`);
    }
  };

  const handleEditCliente = (cliente) => {
    setEditingCliente(cliente);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditingCliente(null);
    setShowModal(false);
  };

  const handleSaveCliente = async (updatedCliente) => {
    try {
      const { _id, ...data } = updatedCliente;
      await patch(`clientes/${_id}`, data);
      fetchClientes();
      handleCloseModal();
      Swal.fire({
        title: 'Éxito',
        text: 'Cliente actualizado correctamente',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleAccumulatePoints = () => {
    fetchClientes(); // ✅ Actualiza la lista de clientes sin hacer llamadas al backend
    handleCloseAccumulateModal(); // ✅ Cierra el modal
  }

  const handleRedeemPoints = async (cliente) => {
    try {
      await patch(`tickets/${cliente.tickets[cliente.tickets.length - 1]._id}/redeem`);
      fetchClientes();
      handleCloseAccumulateModal();
      Swal.fire({
        title: 'Éxito',
        text: 'Puntos canjeados correctamente',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleOpenAccumulateModal = async (cliente) => {
    try {
      const lastTicket = cliente.tickets[cliente.tickets.length - 1];
      setSelectedCliente({ ...cliente, ticket: lastTicket });

      // Obtener la información completa de la marca
      try {
        const marcaCompleta = await get(`marcas/${cliente.marca._id}`);
        setMarcaCompleta(marcaCompleta);
      } catch (error) {
        console.error('Error obteniendo marca completa:', error);
        // Usar la marca básica como fallback
        setMarcaCompleta(cliente.marca);
      }

      setShowAccumulateModal(true);
    } catch (error) {
      console.error('Error handling ticket:', error);
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleOpenRedeemModal = (cliente) => {
    try {
      const lastTicket = cliente.tickets[cliente.tickets.length - 1];
      setSelectedCliente({ ...cliente, ticket: lastTicket });
      setShowRedeemModal(true);
    } catch (error) {
      console.error('Error handling ticket:', error);
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleCloseRedeemModal = () => {
    setSelectedCliente(null);
    setShowRedeemModal(false);
  };

  const handleCloseAccumulateModal = () => {
    setSelectedCliente(null);
    setMontoConsumido(0);
    setMarcaCompleta(null);
    setShowAccumulateModal(false);
  };

  const handleOpenQuickAccumulateModal = async (cliente) => {
    try {
      const lastTicket = cliente.tickets[cliente.tickets.length - 1];
      setSelectedCliente({ ...cliente, ticket: lastTicket });

      // Obtener la información completa de la marca
      try {
        const marcaCompleta = await get(`marcas/${cliente.marca._id}`);
        setMarcaCompleta(marcaCompleta);
      } catch (error) {
        console.error('Error obteniendo marca completa:', error);
        // Usar la marca básica como fallback
        setMarcaCompleta(cliente.marca);
      }

      setShowQuickAccumulateModal(true);
    } catch (error) {
      console.error('Error handling ticket:', error);
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  const handleCloseQuickAccumulateModal = () => {
    setSelectedCliente(null);
    setMarcaCompleta(null);
    setShowQuickAccumulateModal(false);
  };

  const handleQuickAccumulate = (points, monto) => {
    fetchClientes(); // Actualiza la lista de clientes
    handleCloseQuickAccumulateModal(); // Cierra el modal
  };

  const handleRedeemSuccess = () => {
    setSelectedCliente(null);
    setShowRedeemModal(false);
    fetchClientes();
  };


  const handleViewHistory = (cliente) => {
    const clienteId = cliente._id;
    navigate(`/history/${clienteId}`);
  };

  const handleViewPrizeHistory = (cliente) => {
    const clienteId = cliente._id;
    navigate(`/canjeos/${clienteId}`);
  };

  // Obtener los clientes actuales
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClientes.slice(indexOfFirstClient, indexOfLastClient);

  // Cambiar de página
  const paginate = pageNumber => setCurrentPage(pageNumber);

  return (
    <div className="flex flex-col min-h-screen bg-white p-4 md:p-6 rounded-lg shadow-md">
      <h1 className="text-4xl font-bold mb-4">Clientes</h1>
      <button
        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4 mr-4"
        onClick={handleCreateCliente}
      >
        Crear Cliente
      </button>
      <input
        type="text"
        placeholder="Buscar cliente"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-4 p-2 border border-gray-300 rounded"
      />
      {loadingProductos && (
        <p className="mb-4 text-gray-500">Cargando productos...</p>
      )}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentClients.map((cliente) => (
          <div key={cliente._id} className="border p-4 rounded mb-4 relative">
            <h2 className="text-2xl font-bold">{cliente.nombre} {cliente.apellido}</h2>
            <p>Email: {cliente.email}</p>
            <p>Teléfono: {cliente.telefono}</p>
            <p>Fecha de Fidelización: {new Date(cliente.fechaCreacion).toLocaleDateString()}</p>
            <p>Fecha de Cumpleaños: {new Date(cliente.fechaCumpleanos).toLocaleDateString()}</p>
            {cliente.tickets.length > 0 && cliente.tickets[cliente.tickets.length - 1] && (
              cliente.tickets[cliente.tickets.length - 1].tipoFidelizacion === 'PUNTOS' ? (
                <div className="flex space-x-1">
                  {(() => {
                    const ultimoTicket = cliente.tickets[cliente.tickets.length - 1];
                    const puntosRaw = ultimoTicket.puntos;
                    const puntosTicketMarcaRaw = cliente.marca?.puntosTicket;

                    const puntos = Math.max(0, Math.floor(puntosRaw || 0));
                    const puntosTotales = Math.max(0, Math.floor(puntosTicketMarcaRaw || 0));
                    const puntosRestantes = Math.max(0, puntosTotales - puntos);


                    return (
                      <>
                        {puntos > 0 && [...Array(puntos)].map((_, i) => (
                          <FaStar key={`filled-${i}`} className="text-yellow-500" />
                        ))}
                        {puntosRestantes > 0 && [...Array(puntosRestantes)].map((_, i) => (
                          <FaStar key={`empty-${i}`} className="text-gray-300" />
                        ))}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  {(() => {
                    const ticket = cliente.tickets[cliente.tickets.length - 1];

                    // CÁLCULO IDÉNTICO AL DE TICKETVIEWCONTENT.JSX
                    // Para tipo ACUMULACION: usar ticket.puntos directamente
                    // La marca decide cuántos puntos acumula el cliente, no se calculan desde historial
                    // ticket.puntos es la fuente de verdad (puntos disponibles para canjear)
                    const puntosRaw = ticket?.puntos;
                    const puntosDisponibles = Math.floor(puntosRaw || 0);

                    return (
                      <>
                        <p>Puntos Disponibles: {puntosDisponibles}</p>
                      </>
                    );
                  })()}
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2"
                    onClick={() => handleOpenRedeemModal(cliente)}
                  >
                    Canjear Puntos
                  </button>
                </div>
              )
            )}
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => handleEditCliente(cliente)}
            >
              <FaEdit size={20} />
            </button>
            {cliente.tickets.length > 0 && cliente.tickets[cliente.tickets.length - 1] && (
              cliente.tickets[cliente.tickets.length - 1].tipoFidelizacion === 'PUNTOS' ? (
                cliente.tickets[cliente.tickets.length - 1].puntos >= cliente.marca.puntosTicket ? (
                  <>
                    <button
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-700 mt-2"
                      onClick={() => handleRedeemPoints(cliente)}
                    >
                      Canjear Tarjeta
                    </button>
                    <RedeemRewardButton clienteId={cliente._id} recompensaId={cliente.recompensaId} />
                  </>
                ) : (
                  <>
                    {/* Botón Suma Rápida - Mostrar siempre si hay marca (el modal verificará si tiene motor) */}
                    {cliente.marca && cliente.marca._id && (
                      <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2"
                        onClick={() => handleOpenQuickAccumulateModal(cliente)}
                      >
                        ⚡ Suma Rápida
                      </button>
                    )}

                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 mt-2"
                      onClick={() => handleOpenAccumulateModal(cliente)}
                    >
                      Acumular Puntos
                    </button>

                    <RedeemRewardButton clienteId={cliente._id} recompensaId={cliente.recompensaId} />
                  </>
                )
              ) : (
                <>
                  {/* Botón Suma Rápida - Mostrar siempre si hay marca (el modal verificará si tiene motor) */}
                  {cliente.marca && cliente.marca._id && (
                    <button
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2"
                      onClick={() => handleOpenQuickAccumulateModal(cliente)}
                    >
                      ⚡ Suma Rápida
                    </button>
                  )}

                  <button
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 mt-2"
                    onClick={() => handleOpenAccumulateModal(cliente)}
                  >
                    Acumular Puntos
                  </button>
                </>
              )
            )}

            {/* Botón Condicional para Ver Historial de Canjeos de Premios */}
            {cliente.tickets.length > 0 && cliente.tickets[cliente.tickets.length - 1] && (
              cliente.tickets[cliente.tickets.length - 1].tipoFidelizacion === 'ACUMULACION' && (
                <button
                  className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-700 mt-2 ml-2"
                  onClick={() => handleViewPrizeHistory(cliente)}
                >
                  Ver Historial de Canjeos
                </button>
              )
            )}


            {!trabajadorId && (
              <button
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-700 mt-2"
                onClick={() => handleViewHistory(cliente)}
              >
                Ver Historial de puntos
              </button>
            )}
          </div>
        ))}
      </div>
      <Pagination
        clientsPerPage={clientsPerPage}
        totalClients={filteredClientes.length}
        paginate={paginate}
        currentPage={currentPage}
      />
      {showModal && (
        <EditClienteModal
          cliente={editingCliente}
          onClose={handleCloseModal}
          onSave={handleSaveCliente}
        />
      )}

      {showRedeemModal && selectedCliente && (
        <RedeemPremiosModal
          cliente={selectedCliente}
          onClose={handleCloseRedeemModal}
          trabajadorId={trabajadorId}
          onRedeemSuccess={handleRedeemSuccess}
        />
      )}

      {showAccumulateModal && selectedCliente && (
        <AccumulatePointsModal
          ticketId={selectedCliente.ticket._id}
          montoConsumido={montoConsumido}
          setMontoConsumido={setMontoConsumido}
          puntosMaximos={selectedCliente.marca.puntosTicket}
          puntosAcumulados={selectedCliente.ticket.puntos}
          onClose={handleCloseAccumulateModal}
          onAccumulate={handleAccumulatePoints}
          onRedeem={handleRedeemPoints}
          tipoFidelizacion={selectedCliente.ticket.tipoFidelizacion}
          clienteId={selectedCliente._id}
          productos={productos}
          tiendaId={tiendaId} // ✅ PASAR TIENDA ID
          marca={marcaCompleta || selectedCliente.marca} // ✅ PASAR MARCA COMPLETA PARA MOTOR DE PUNTOS
        />
      )}

      {showQuickAccumulateModal && selectedCliente && (
        <QuickAccumulateModal
          ticketId={selectedCliente.ticket._id}
          puntosMaximos={selectedCliente.marca.puntosTicket}
          puntosAcumulados={selectedCliente.ticket.puntos}
          onClose={handleCloseQuickAccumulateModal}
          onAccumulate={handleQuickAccumulate}
          onOpenFullModal={() => {
            setShowQuickAccumulateModal(false);
            handleOpenAccumulateModal(selectedCliente);
          }}
          tipoFidelizacion={selectedCliente.ticket.tipoFidelizacion}
          clienteId={selectedCliente._id}
          tiendaId={tiendaId}
          marca={marcaCompleta || selectedCliente.marca}
        />
      )}
    </div>
  );
}

export default ClientesList;
